import { useEffect, useRef, useState } from 'react';
import styles from './Argument.module.css';

export type Beat = 'hidden' | 'shown';

const BEAT_MS: Record<Beat, number> = { hidden: 2400, shown: 5200 };

export function useBeat(enabled: boolean): Beat {
  const [beat, setBeat] = useState<Beat>('hidden');
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setBeat('shown');
      return;
    }

    let current: Beat = 'hidden';
    setBeat(current);

    const tick = () => {
      timer.current = window.setTimeout(() => {
        current = current === 'hidden' ? 'shown' : 'hidden';
        setBeat(current);
        tick();
      }, BEAT_MS[current]);
    };

    tick();
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [enabled]);

  return beat;
}

export function useReducedMotionAware(): boolean {
  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setAnimate(!query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  return animate;
}

/**
 * One card, built like the room's: a real 3D turn with a front and a back.
 * `front` is what shows at rest, `back` is what it turns over to.
 */
export function FlipCard({
  front,
  back,
  flipped,
  delayMs,
  facedownFront,
  tone,
  text,
}: {
  readonly front: string;
  readonly back: string;
  readonly flipped: boolean;
  readonly delayMs: number;
  readonly facedownFront?: boolean;
  readonly tone?: 'split' | 'grouped';
  /** Renders both faces as short prose instead of a single tabular numeral. */
  readonly text?: boolean;
}) {
  const toneClass =
    tone === 'split' ? styles.split : tone === 'grouped' ? styles.grouped : '';

  return (
    <span className={`${styles.slot} ${text ? styles.slotText : ''}`}>
      <span
        className={`${styles.inner} ${flipped ? styles.flipped : ''}`}
        style={{ transitionDelay: `${delayMs}ms` }}
      >
        <span
          className={`${styles.face} ${text ? styles.faceText : ''} ${facedownFront ? styles.back : ''}`}
        >
          {facedownFront ? '' : front}
        </span>
        <span
          className={`${styles.face} ${styles.rear} ${text ? styles.faceText : ''} ${toneClass}`}
        >
          {back}
        </span>
      </span>
    </span>
  );
}
